// Roles API client — v2 typed client.
//
// CHG-1663 / ITER-3772 (Wave 1 / Tier 1 — Roles port; generator-emitted
// pages consume `rolesApi` namespace via the v2 page generator name
// convention `<entityCamel>sApi`).
//
// Backend: src/api/app/routers/roles.py + permission catalog endpoint.
// /api/v1/roles list endpoint returns PagedEnvelope { data, meta }
// per CHG-0879 — the list wrapper here projects to v2's
// { items, total }.
//
// Shape: a `rolesApi` object with the four methods the generator
// calls (listRoles + getRole) plus the additional mutations + the
// permission-catalog / role-permission accessors used by the custom
// override-block PermissionMatrix + RoleFormModal components.

import { apiClient } from "./client";

export interface Role {
  id: string;
  organization_id: string | null;
  slug: string;
  display_name: string;
  description?: string | null;
  is_system: boolean;
  is_active: boolean;
  priority: number;
  permission_count: number;
  user_count: number;
}

export interface PermissionInfo {
  code: string;
  display_name: string;
  description: string;
  module: string;
}

export interface RoleCreatePayload {
  slug: string;
  display_name: string;
  description?: string | null;
  priority?: number;
}

export interface RoleUpdatePayload {
  display_name?: string | null;
  description?: string | null;
  is_active?: boolean | null;
  priority?: number | null;
}

interface RolePagedEnvelope {
  data: Role[];
  meta?: {
    page?: number;
    per_page?: number;
    total?: number;
    pages?: number;
  };
}

export interface ListRolesParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
}

export const rolesApi = {
  async listRoles(
    params: ListRolesParams = {},
  ): Promise<{ items: Role[]; total: number }> {
    // Backend uses 1-indexed `page` + `per_page`. v2 ListPage passes
    // 0-indexed `page` so we add 1. Backend caps per_page at 200.
    const r = await apiClient().get<RolePagedEnvelope>("/roles", {
      params: {
        page: (params.page ?? 0) + 1,
        per_page: params.pageSize ?? 100,
        ...(params.search ? { search: params.search } : {}),
      },
    });
    return {
      items: Array.isArray(r.data?.data) ? r.data.data : [],
      total: r.data?.meta?.total ?? 0,
    };
  },

  async getRole(id: string): Promise<Role> {
    const r = await apiClient().get<Role>(`/roles/${id}`);
    return r.data;
  },

  async createRole(payload: RoleCreatePayload): Promise<Role> {
    const r = await apiClient().post<Role>("/roles", payload);
    return r.data;
  },

  async updateRole(id: string, payload: RoleUpdatePayload): Promise<Role> {
    const r = await apiClient().patch<Role>(`/roles/${id}`, payload);
    return r.data;
  },

  async deleteRole(id: string): Promise<void> {
    await apiClient().delete(`/roles/${id}`);
  },

  async listPermissions(): Promise<PermissionInfo[]> {
    const r = await apiClient().get<PermissionInfo[]>("/permissions");
    return Array.isArray(r.data) ? r.data : [];
  },

  async getRolePermissions(roleId: string): Promise<string[]> {
    const r = await apiClient().get<string[]>(`/roles/${roleId}/permissions`);
    return Array.isArray(r.data) ? r.data : [];
  },

  async setRolePermissions(
    roleId: string,
    permissions: string[],
  ): Promise<string[]> {
    // PUT (full replace per backend contract); backend validates each
    // code is held by the caller before granting.
    const r = await apiClient().put<string[]>(`/roles/${roleId}/permissions`, {
      permissions,
    });
    return Array.isArray(r.data) ? r.data : [];
  },
};
